import React, { useMemo } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, X } from "lucide-react";
import {
  useGetTagsQuery,
  useGetDealStagesQuery,
  useGetMembersQuery,
  useGetAiAgentsQuery,
  useGetCampaignsQuery,
} from "@/store/rest_api";
import type { WorkflowActionConfigValue, WorkflowActionType } from "@/model/interface/Workflow";
import { configNumberOrEmpty, configString, extractOutcomeRouting } from "./configValueHelpers";
import SmsBlockedNotice from "@/components/messages/SmsBlockedNotice";
import MailingAddressNotice from "@/components/messages/MailingAddressNotice";
import { memberName } from "@/util/member";

interface ActionConfigEditorProps {
  actionType: WorkflowActionType;
  config: Record<string, WorkflowActionConfigValue>;
  onConfigChange: (key: string, value: WorkflowActionConfigValue) => void;
}

const ActionConfigEditor: React.FC<ActionConfigEditorProps> = ({ actionType, config, onConfigChange }) => {
  const { data: tags = [] } = useGetTagsQuery({});
  const { data: stages = [] } = useGetDealStagesQuery({});
  const { data: members = [] } = useGetMembersQuery({ status: "ACTIVE" });
  const { data: aiAgents = [] } = useGetAiAgentsQuery(undefined, {
    skip: actionType !== "PLACE_AI_CALL",
  });
  const { data: campaigns = [] } = useGetCampaignsQuery(undefined, {
    skip: actionType !== "ENQUEUE_POWER_DIAL",
  });
  const selectedAgent = useMemo(
    () => aiAgents.find((agent) => agent.id === configString(config.agentId)),
    [aiAgents, config.agentId],
  );

  const outcomeRouting = useMemo(() => extractOutcomeRouting(config.onOutcome), [config.onOutcome]);

  const setOutcomeRoute = (outcomeName: string, nextSortOrder: number | null) => {
    onConfigChange("onOutcome", { ...outcomeRouting, [outcomeName]: nextSortOrder });
  };

  const removeOutcomeRoute = (outcomeName: string) => {
    const updated = { ...outcomeRouting };
    delete updated[outcomeName];
    onConfigChange("onOutcome", updated);
  };

  switch (actionType) {
    case "SEND_SMS":
      return (
        <div className="space-y-3">
          <SmsBlockedNotice />
          <div>
            <Label className="text-xs">
              Message (use {"{{firstName}}"}, {"{{lastName}}"}, {"{{company}}"}, {"{{phone}}"}, {"{{email}}"}, {"{{website}}"})
            </Label>
            <Textarea
              rows={2}
              placeholder="Hi {{firstName}}, ..."
              value={configString(config.message)}
              onChange={(changeEvent) => onConfigChange("message", changeEvent.target.value)}
              className="mt-1"
            />
          </div>
          <div className="flex items-start gap-2">
            <Checkbox
              id="require-sms-consent"
              checked={config.requireSmsConsent === true}
              onCheckedChange={(value) => onConfigChange("requireSmsConsent", value === true)}
              className="mt-0.5"
            />
            <div className="space-y-0.5">
              <Label htmlFor="require-sms-consent" className="text-xs">
                Only send to contacts with SMS consent
              </Label>
              <p className="text-muted-foreground text-[11px]">
                When on, this step is skipped for numbers without SMS consent on file. Quiet hours (8am to 9pm in
                the contact's timezone) always apply and reschedule the message to the next morning.
              </p>
            </div>
          </div>
        </div>
      );

    case "SEND_EMAIL":
      return (
        <div className="space-y-2">
          <MailingAddressNotice />
          <div>
            <Label className="text-xs">To (leave empty to use contact email)</Label>
            <Input
              placeholder="recipient@example.com"
              value={configString(config.to)}
              onChange={(changeEvent) => onConfigChange("to", changeEvent.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Subject</Label>
            <Input
              placeholder="Welcome to {{company}}!"
              value={configString(config.subject)}
              onChange={(changeEvent) => onConfigChange("subject", changeEvent.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Body</Label>
            <Textarea
              rows={3}
              placeholder="Hi {{firstName}}, ..."
              value={configString(config.body)}
              onChange={(changeEvent) => onConfigChange("body", changeEvent.target.value)}
              className="mt-1"
            />
          </div>
        </div>
      );

    case "CREATE_TASK":
      return (
        <div className="space-y-2">
          <div>
            <Label className="text-xs">Task Title</Label>
            <Input
              placeholder="Follow up with {{firstName}}"
              value={configString(config.title)}
              onChange={(changeEvent) => onConfigChange("title", changeEvent.target.value)}
              className="mt-1"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Priority</Label>
              <Select
                value={configString(config.priority) || "MEDIUM"}
                onValueChange={(priority) => onConfigChange("priority", priority)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LOW">Low</SelectItem>
                  <SelectItem value="MEDIUM">Medium</SelectItem>
                  <SelectItem value="HIGH">High</SelectItem>
                  <SelectItem value="URGENT">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Due in (days)</Label>
              <Input
                type="number"
                min={0}
                value={configNumberOrEmpty(config.dueDaysOffset) || 1}
                onChange={(changeEvent) => onConfigChange("dueDaysOffset", changeEvent.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Assign to</Label>
            <Select
              value={configString(config.assignedMembershipId)}
              onValueChange={(membershipId) =>
                onConfigChange("assignedMembershipId", membershipId === "NONE" ? "" : membershipId)
              }
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NONE">Unassigned</SelectItem>
                {members.map((member) => (
                  <SelectItem key={member.id} value={String(member.id)}>
                    {memberName(member)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      );

    case "ADD_TAG":
    case "REMOVE_TAG":
      return (
        <div>
          <Label className="text-xs">Tag</Label>
          <Select value={configString(config.tagId)} onValueChange={(tagId) => onConfigChange("tagId", tagId)}>
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select tag" />
            </SelectTrigger>
            <SelectContent>
              {tags.map((tag) => (
                <SelectItem key={tag.id} value={tag.id}>
                  {tag.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );

    case "MOVE_DEAL_STAGE":
      return (
        <div>
          <Label className="text-xs">Move to stage</Label>
          <Select
            value={configString(config.stageId)}
            onValueChange={(stageId) => onConfigChange("stageId", stageId)}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select stage" />
            </SelectTrigger>
            <SelectContent>
              {stages.map((stage) => (
                <SelectItem key={stage.id} value={stage.id}>
                  {stage.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );

    case "ASSIGN_TO_MEMBER":
      return (
        <div>
          <Label className="text-xs">Assign to member</Label>
          <Select
            value={configString(config.membershipId)}
            onValueChange={(membershipId) => onConfigChange("membershipId", membershipId)}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select member" />
            </SelectTrigger>
            <SelectContent>
              {members.map((member) => (
                <SelectItem key={member.id} value={String(member.id)}>
                  {memberName(member)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );

    case "SEND_WEBHOOK":
      return (
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Webhook URL</Label>
            <Input
              placeholder="https://..."
              value={configString(config.url)}
              onChange={(changeEvent) => onConfigChange("url", changeEvent.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Signing secret (optional)</Label>
            <Input
              placeholder="Used to sign payloads with HMAC SHA-256"
              value={configString(config.secret)}
              onChange={(changeEvent) => onConfigChange("secret", changeEvent.target.value)}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              When set, each delivery includes an X-Retreevo-Signature header you can verify on your server.
            </p>
          </div>
        </div>
      );

    case "PLACE_AI_CALL":
      return (
        <div className="space-y-3">
          <div>
            <Label className="text-xs">AI Agent *</Label>
            <Select
              value={configString(config.agentId)}
              onValueChange={(agentId) => onConfigChange("agentId", agentId)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Choose an AI agent" />
              </SelectTrigger>
              <SelectContent>
                {aiAgents.map((agent) => (
                  <SelectItem key={agent.id} value={agent.id}>
                    {agent.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Call scope (optional)</Label>
            <Textarea
              rows={3}
              placeholder="E.g. 'Confirm the 3pm Friday appointment. Reschedule if needed.'"
              value={configString(config.callScope)}
              onChange={(changeEvent) => onConfigChange("callScope", changeEvent.target.value)}
              className="mt-1"
            />
            <p className="text-muted-foreground mt-1 text-xs">
              Appended to the agent's system prompt for this call only.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">From phone number ID (optional)</Label>
              <Input
                placeholder="Defaults to org's first active"
                value={configString(config.fromPhoneNumberId)}
                onChange={(changeEvent) => onConfigChange("fromPhoneNumberId", changeEvent.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Max duration (seconds)</Label>
              <Input
                type="number"
                min={0}
                placeholder="Unlimited"
                value={configNumberOrEmpty(config.maxDurationSeconds)}
                onChange={(changeEvent) =>
                  onConfigChange(
                    "maxDurationSeconds",
                    changeEvent.target.value ? parseInt(changeEvent.target.value, 10) : null,
                  )
                }
                className="mt-1"
              />
            </div>
          </div>

          <OutcomeRoutingEditor
            agentDeclaredOutcomes={selectedAgent?.declaredOutcomes ?? []}
            outcomeRouting={outcomeRouting}
            onSetRoute={setOutcomeRoute}
            onRemoveRoute={removeOutcomeRoute}
          />
        </div>
      );

    case "ENQUEUE_POWER_DIAL":
      return (
        <div>
          <Label className="text-xs">Campaign *</Label>
          {campaigns.length === 0 ? (
            <p className="text-muted-foreground mt-1 text-xs">
              No campaigns yet. Create a power dialer or sequence campaign first, then route leads to it here.
            </p>
          ) : (
            <Select
              value={configString(config.campaignId)}
              onValueChange={(campaignId) => onConfigChange("campaignId", campaignId)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select a campaign" />
              </SelectTrigger>
              <SelectContent>
                {campaigns.map((campaign) => (
                  <SelectItem key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <p className="text-muted-foreground mt-1 text-xs">
            Adds the lead to a power dialer campaign (for an agent to call) or a sequence campaign (to enroll in a drip).
          </p>
        </div>
      );

    case "SEND_NOTIFICATION":
    case "SEND_PUSH":
      return (
        <div className="space-y-2">
          <div>
            <Label className="text-xs">Notify</Label>
            <Select
              value={configString(config.recipientMode) || "ASSIGNEE"}
              onValueChange={(value) => onConfigChange("recipientMode", value)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ASSIGNEE">The task assignee / record owner</SelectItem>
                <SelectItem value="MEMBER">A specific member</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {configString(config.recipientMode) === "MEMBER" && (
            <div>
              <Label className="text-xs">Member</Label>
              <Select
                value={configString(config.membershipId)}
                onValueChange={(membershipId) => onConfigChange("membershipId", membershipId)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select member" />
                </SelectTrigger>
                <SelectContent>
                  {members.map((member) => (
                    <SelectItem key={member.id} value={String(member.id)}>
                      {memberName(member)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">Title</Label>
            <Input
              placeholder="Task due"
              value={configString(config.title)}
              onChange={(changeEvent) => onConfigChange("title", changeEvent.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Message</Label>
            <Textarea
              rows={2}
              placeholder="You have a task that needs attention."
              value={configString(config.message)}
              onChange={(changeEvent) => onConfigChange("message", changeEvent.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Link (optional)</Label>
            <Input
              placeholder="/tasks"
              value={configString(config.link)}
              onChange={(changeEvent) => onConfigChange("link", changeEvent.target.value)}
              className="mt-1"
            />
          </div>
        </div>
      );

    default:
      return null;
  }
};

interface OutcomeRoutingEditorProps {
  agentDeclaredOutcomes: string[];
  outcomeRouting: Record<string, number | null>;
  onSetRoute: (outcomeName: string, nextSortOrder: number | null) => void;
  onRemoveRoute: (outcomeName: string) => void;
}

const OutcomeRoutingEditor: React.FC<OutcomeRoutingEditorProps> = ({
  agentDeclaredOutcomes,
  outcomeRouting,
  onSetRoute,
  onRemoveRoute,
}) => {
  const [newOutcomeName, setNewOutcomeName] = React.useState("");

  const routedOutcomes = Object.keys(outcomeRouting);
  const builtInOutcomes = ["NO_ANSWER", "VOICEMAIL", "BUSY", "ERROR", "HANGUP"];
  const allOutcomes = Array.from(new Set([...agentDeclaredOutcomes, ...builtInOutcomes, ...routedOutcomes]));

  const addCustomOutcome = () => {
    const trimmed = newOutcomeName.trim().toUpperCase();
    if (!trimmed || outcomeRouting[trimmed] !== undefined) {
      return;
    }
    onSetRoute(trimmed, null);
    setNewOutcomeName("");
  };

  return (
    <div className="border rounded-lg p-3 space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs">Outcome routing</Label>
        {agentDeclaredOutcomes.length > 0 && (
          <span className="text-muted-foreground text-[10px]">
            Agent declares: {agentDeclaredOutcomes.join(", ")}
          </span>
        )}
      </div>
      <p className="text-muted-foreground text-[11px]">
        Decide what happens after the call ends with each outcome. Leave a row empty to fall through to the next
        sequential step. Choose "End workflow" to stop further steps.
      </p>

      <div className="space-y-1.5">
        {allOutcomes.map((outcomeName) => {
          const currentRoute = outcomeRouting[outcomeName];
          const hasRoute = outcomeName in outcomeRouting;
          return (
            <div key={outcomeName} className="flex items-center gap-2">
              <Badge variant="outline" className="min-w-[9rem] justify-start">
                {outcomeName}
                {agentDeclaredOutcomes.includes(outcomeName) && (
                  <span className="text-muted-foreground ml-1 text-[9px]">(agent)</span>
                )}
              </Badge>
              <Select
                value={hasRoute ? (currentRoute === null ? "END" : String(currentRoute)) : "FALLTHROUGH"}
                onValueChange={(routeChoice) => {
                  if (routeChoice === "FALLTHROUGH") {
                    onRemoveRoute(outcomeName);
                  } else if (routeChoice === "END") {
                    onSetRoute(outcomeName, null);
                  } else {
                    const parsedSortOrder = parseInt(routeChoice, 10);
                    if (!Number.isNaN(parsedSortOrder)) {
                      onSetRoute(outcomeName, parsedSortOrder);
                    }
                  }
                }}
              >
                <SelectTrigger className="flex-1 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FALLTHROUGH">Fall through to next step</SelectItem>
                  <SelectItem value="END">End workflow</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="number"
                min={0}
                placeholder="Or sortOrder"
                className="w-28 text-xs"
                value={hasRoute && typeof currentRoute === "number" ? currentRoute : ""}
                onChange={(changeEvent) => {
                  const rawValue = changeEvent.target.value;
                  if (rawValue === "") {
                    onRemoveRoute(outcomeName);
                  } else {
                    const parsedSortOrder = parseInt(rawValue, 10);
                    if (!Number.isNaN(parsedSortOrder)) {
                      onSetRoute(outcomeName, parsedSortOrder);
                    }
                  }
                }}
              />
              {hasRoute && (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => onRemoveRoute(outcomeName)}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex gap-2 pt-1">
        <Input
          placeholder="Add another outcome (e.g. INTERESTED)"
          value={newOutcomeName}
          onChange={(changeEvent) => setNewOutcomeName(changeEvent.target.value)}
          onKeyDown={(keyEvent) => {
            if (keyEvent.key === "Enter") {
              keyEvent.preventDefault();
              addCustomOutcome();
            }
          }}
          className="text-xs"
        />
        <Button type="button" size="sm" variant="outline" onClick={addCustomOutcome}>
          <Plus className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
};

export default ActionConfigEditor;
